import { useMemo } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import { useLoyaltyBadgesQuery } from 'generated/graphql-gateway';
import { useLocale } from 'state/intl';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';

import { ILoyaltyBadge } from './types';
import { getBadgeProgressPercentage } from './utils';

export const useGetLoyaltyBadges = ({
  skip = false,
  fetchPolicy = 'cache-and-network',
}: { skip?: boolean; fetchPolicy?: WatchQueryFetchPolicy } = {}) => {
  const { language } = useLocale();
  const { loyaltyUserId } = useLoyaltyUserId();
  const { loading, data, error, refetch } = useLoyaltyBadgesQuery({
    fetchPolicy,
    variables: { loyaltyId: loyaltyUserId, where: { fetchAll: true } },
    skip: skip || !loyaltyUserId,
  });

  // With fetchPolicy 'cache-and-network', Apollo sets loading=true during background
  // refetches even when cached data is already available. Use initialLoading to gate
  // skeletons/loaders so cached content is shown immediately on warm-cache mounts.
  const initialLoading = loading && !data?.loyaltyUserV2?.badges;

  const badges: ILoyaltyBadge[] = useMemo(() => {
    const mapped = (data?.loyaltyUserV2?.badges ?? []).map(badge => ({
      id: badge.id,
      earnedAt: badge.earnedAt,
      name: badge.name?.[language] ?? '',
      description: badge.description?.[language],
      imageId: badge.image?.[language]?.imageUrl,
      imageAlt: badge.image?.[language]?.imageAlt,
      lottie: badge.lottie,
      shortDescription: badge.shortDescription?.[language],
      terms: badge.terms?.[language],
      lockedCtaLink: badge.ctaLink?.[language],
      lockedCtaText: badge.ctaText?.[language],
      progress: badge.progress
        ? {
            currentValue: badge.progress.currentValue,
            targetValue: badge.progress.targetValue,
            itemLabel: badge.progress.itemLabel?.[language],
            encouragingPhrase: badge.progress.encouragingPhrase?.[language],
          }
        : undefined,
    }));

    // Earned badges appear first, sorted by earnedAt descending (most recently earned first).
    // Unearned badges follow, sorted by normalized progress percentage descending (closest to
    // completion first), then by raw currentValue as a tie-breaker, then by original server order.
    // This ensures a stable display order regardless of fetch policy or server response ordering.
    return mapped.sort((a, b) => {
      if (a.earnedAt && !b.earnedAt) {
        return -1;
      }
      if (!a.earnedAt && b.earnedAt) {
        return 1;
      }
      if (a.earnedAt && b.earnedAt) {
        return new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime();
      }
      const aPercentage = a.progress ? getBadgeProgressPercentage(a.progress) : 0;
      const bPercentage = b.progress ? getBadgeProgressPercentage(b.progress) : 0;
      if (bPercentage !== aPercentage) {
        return bPercentage - aPercentage;
      }
      return (b.progress?.currentValue ?? 0) - (a.progress?.currentValue ?? 0);
    });
  }, [language, data?.loyaltyUserV2?.badges]);

  return {
    badges,
    error,
    initialLoading,
    loading,
    refetch,
  };
};
